import { InputDateModal } from "core/inputs/input-date/InputDateModal";
import InputSearchList from "core/inputs/input-search/InputSearchList";
import _ from "lodash";
import { FORM_INFO } from "constants/forms";
import { FormattedMessage } from "react-intl";
import { Table } from "core/table/Table";
import { Typography } from "core/typography/Typography";
import { phoneSize } from "utils/screenSizes";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  onAdd,
  onChange,
  onChangeInputSubCompany,
  onDelete,
  renderCommonBlock,
} from "utils/tableContentHelper";
import { headerGenerator } from "utils/HeaderHelper";
import PropTypes from "prop-types";
import { format } from "date-fns";
import { MODAL_CONTENT } from "constants/constants";

const ManageBillingClientHistory = ({
  userScope,
  billingClientHistory,
  billingClientsByName,
  billingClientsByIds,
  getOptions,
  getUpdatedValues,
  lang,
  usersByIds,
  setElementsToDelete,
  clientBySlug,
}) => {
  const headerParams = [
    {
      key: "billing_client",
      defaultMessage: "Société de facturation",
      icon: "user",
      paddingLeft: ".75rem",
    },
    {
      key: "siren",
      defaultMessage: "Siren",
      icon: "calendar",
      paddingLeft: ".75rem",
    },
    {
      key: "establishment_date",
      defaultMessage: "Date d'application",
      icon: "calendar",
    },
    { key: "updated_by", defaultMessage: "Modifié par", icon: "user" },
    { key: "updated_at", defaultMessage: "le", icon: "calendar" },
  ];

  const header = headerGenerator(headerParams);
  const [initialData, setInitialData] = useState(billingClientHistory);
  const [data, setData] = useState(billingClientHistory);
  const [formattedBillingClients, setFormattedBillingClients] = useState([]);

  useEffect(() => {
    const result = _.chain(billingClientsByName)
      .concat(billingClientsByIds)
      .map((elm) => ({
        label: elm.name,
        value: elm.id,
      }))
      .value();
    setFormattedBillingClients(
      _.chain(result)
        .concat(formattedBillingClients)
        .flattenDeep()
        .uniqWith(_.isEqual)
        .compact()
        .value()
    );
  }, [billingClientsByIds, billingClientsByName]);

  useEffect(() => {
    if (
      initialData.length < billingClientHistory?.length &&
      billingClientHistory?.length > 0
    ) {
      setInitialData(billingClientHistory);
    }
  }, [initialData, billingClientHistory]);

  useEffect(() => {
    if (
      _.isEqual(initialData, billingClientHistory) &&
      initialData.length > 0
    ) {
      setData(
        _.map(initialData, (elm) => ({
          ...elm,
          updated_by: usersByIds?.find((usr) => usr?.id === elm?.updated_by)
            ?.name,
        }))
      );
    }
  }, [initialData, usersByIds]);

  const formatContent = (content) =>
    content?.length > 0
      ? content?.map((elm, index) => {
          const dataTestIndex = content.length - 1 - index;
          return {
            id: elm?.id,
            billing_client_id: (
              <InputSearchList
                limitChoice={1}
                onDelete={onDelete}
                options={formattedBillingClients}
                popup={FORM_INFO.historyCompany.popup}
                selectedOptions={formattedBillingClients?.filter(
                  (br) => br?.value === elm?.billing_client_id
                )}
                minLength={3}
                getOptions={getOptions}
                setValue={(val) => {
                  onChange({
                    getUpdatedValues,
                    id: elm?.id,
                    key: "billing_client_id",
                    setData,
                    data,
                    val,
                  });
                }}
                onChangeInput={() => {
                  onChangeInputSubCompany({
                    getUpdatedValues,
                    id: elm?.id,
                    setData,
                    data,
                  });
                }}
                dataTestId={`${MODAL_CONTENT.MANAGE_BILLING_CLIENT_HISTORY}_modal-billing_client-${dataTestIndex}`}
              />
            ),
            siren: _.chain(billingClientsByIds)
              .concat(billingClientsByName)
              .find((e) => e.id === elm.billing_client_id)
              .get("siren")
              .value(),

            establishment_date: (
              <div
                data-testid={`${MODAL_CONTENT.MANAGE_BILLING_CLIENT_HISTORY}_modal-establishment_date-${dataTestIndex}`}
              >
                <InputDateModal
                  lang={lang}
                  elm={elm}
                  onChange={(date) => {
                    const formattedDate = date
                      ? format(date, "yyyy-MM-dd")
                      : "";
                    onChange({
                      getUpdatedValues,
                      id: elm?.id,
                      key: "establishment_date",
                      setData,
                      data,
                      val: formattedDate,
                    });
                  }}
                />
              </div>
            ),
            ...renderCommonBlock({
              data,
              getUpdatedValues,
              id: elm?.id,
              onDelete,
              setData,
              updated_at: elm.updated_at,
              updated_by: elm.updated_by,
              lang,
              setElementsToDelete,
              deleteBtnTestId: `${MODAL_CONTENT.MANAGE_BILLING_CLIENT_HISTORY}_modal-delete_btn-${dataTestIndex}`,
            }),
          };
        })
      : [];

  return (
    <>
      <div
        style={{ display: "flex", flexDirection: phoneSize ? "column" : "row" }}
      >
        <button
          className="button-style"
          onClick={() => onAdd({ header, data, setData })}
          data-testid={`${MODAL_CONTENT.MANAGE_BILLING_CLIENT_HISTORY}_modal-add_billing_client_history_btn`}
        >
          <Typography
            className="link-1 violet "
            type="h2"
            label={
              <>
                +{" "}
                <FormattedMessage
                  id="add_billing_client_history"
                  defaultMessage="Ajouter un historique sociétaire"
                />
              </>
            }
          />
        </button>
        <Link
          to={`/${userScope}/billing-client/creation`}
          state={{
            clientId: clientBySlug?.id,
            clientSlug: clientBySlug?.slug,
          }}
          className="link"
          data-testid={`${MODAL_CONTENT.MANAGE_COMPANY_HISTORY}_modal-create_and_associate_company_btn`}
        >
          <Typography
            className="link-1 violet "
            type="h2"
            label={
              <>
                +{" "}
                <FormattedMessage
                  id="automatically_create_and_associate_a_new_billing_client"
                  defaultMessage="Créer et associer automatiquement une nouvelle société de facturation"
                />
              </>
            }
          />
        </Link>
      </div>
      <br />
      <Table
        id="billing_client_history"
        header={header}
        content={formatContent(data)}
        tableOverflow={"auto"}
        tableWidth={phoneSize ? "max-content" : "100%"}
      />
    </>
  );
};

export default ManageBillingClientHistory;

ManageBillingClientHistory.propTypes = {
  billingClientHistory: PropTypes.array,
  billingClientsByName: PropTypes.array,
  billingClientsByIds: PropTypes.array,
  getOptions: PropTypes.func,
  getUpdatedValues: PropTypes.func,
  lang: PropTypes.string,
  usersByIds: PropTypes,
  setElementsToDelete: PropTypes.func,
  userScope: PropTypes.string,
  clientBySlug: PropTypes.object,
};
